import React, { useEffect } from 'react';
import AnimatedCounter from '../components/AnimatedCounter';

const VRGroup = () => {
  useEffect(() => {
    // Initialize scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);
    
    const fadeElements = document.querySelectorAll('.fade-in');
    fadeElements.forEach(el => observer.observe(el));

    return () => {
      fadeElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="vr-group-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>VR Group</h1>
          <p>A legacy of excellence since 2016, building a sustainable future through diversified business ventures</p>
        </div>
      </section>

      {/* Group Overview Section */}
      <section className="content-section">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>About VR Group</h2>
              <p>
                VR Group was founded in 2016 by Mr. Ramshibhai Goriya with a vision to create a diversified business conglomerate 
                that contributes to India's growth across multiple sectors. What started as a focused enterprise has evolved into 
                a well-recognized group in Gujarat, spanning renewable energy, mining, minerals, and industrial civil activities.
              </p>
              
              <p>
                Our group is built on the foundation of integrity, innovation, and excellence. We believe in sustainable business 
                practices that not only drive economic growth but also contribute positively to society and the environment. 
                With over 9 years of experience, VR Group has established itself as a trusted partner for large-scale infrastructure 
                and energy projects.
              </p>

              <h3>Our Vision</h3>
              <p>
                To be a leading diversified business group that drives sustainable development across multiple sectors, 
                contributing to India's economic growth while maintaining the highest standards of corporate governance 
                and environmental responsibility.
              </p>

              <h3>Our Mission</h3>
              <p>
                To deliver exceptional value through innovative solutions, strategic partnerships, and unwavering commitment 
                to quality, while fostering sustainable development and creating opportunities for communities we serve.
              </p>
            </div>

            <div className="stats">
              <div className="stat-item">
                <AnimatedCounter end={2016} />
                <span className="stat-label">Founded</span>
              </div>
              <div className="stat-item">
                <AnimatedCounter end={9} suffix="+" />
                <span className="stat-label">Years Experience</span>
              </div>
              <div className="stat-item">
                <AnimatedCounter end={2} />
                <span className="stat-label">Core Companies</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">Multiple</span>
                <span className="stat-label">Business Sectors</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Group Companies Section */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Our Companies</h2>
          <p className="section-subtitle">Building a diversified portfolio of businesses across key industries</p>
          
          <div className="services-grid">
            {/* VReco Energy */}
            <div className="service-card highlighted-service">
              <div className="service-icon highlighted-icon">
                <img src="/img/vr2.png" alt="VReco Energy" style={{ width: '40px', height: '40px', objectFit: 'contain' }} 
                     loading="lazy" decoding="async"
                     onError={(e) => {e.target.style.display='none'; const icon = document.createElement('span'); icon.textContent = '⚡'; e.target.parentElement.appendChild(icon);}} />
              </div>
              <h3>VReco Energy Pvt Ltd</h3>
              <p>
                Our flagship renewable energy company specializing in wind turbine projects, engineering manpower supply, 
                security services, and heavy equipment rental. Leading the transition to sustainable energy solutions.
              </p>
              <ul>
                <li>Wind Turbine Generator (WTG) Projects</li>
                <li>Engineering Manpower & Security Services</li>
                <li>Heavy Equipment Rental</li>
                <li>310+ MW Projects Completed</li>
                <li>150+ MW Projects Ongoing</li>
              </ul>
            </div>
            
            {/* Veer Enterprise */}
            <div className="service-card">
              <div className="service-icon">
                <img src="/img/vr2.png" alt="Veer Enterprise" style={{ width: '40px', height: '40px', objectFit: 'contain' }} 
                     loading="lazy" decoding="async"
                     onError={(e) => {e.target.style.display='none'; const icon = document.createElement('span'); icon.textContent = '⛏️'; e.target.parentElement.appendChild(icon);}} />
              </div>
              <h3>Veer Enterprise</h3>
              <p>
                A key subsidiary of VR Group, focusing on mining & mineral extraction and industrial civil activities. 
                Providing essential raw materials and infrastructure development services to support India's industrial growth.
              </p>
              <ul>
                <li>Mining & Mineral Extraction</li>
                <li>Industrial Civil Construction</li>
                <li>Infrastructure Development</li>
                <li>Raw Material Supply</li>
                <li>Environmental Compliance</li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="content-section">
        <div className="container">
          <h2 className="section-title">Leadership</h2>
          <p className="section-subtitle">Visionary leadership driving sustainable growth and innovation</p>
          
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="director-card" style={{ maxWidth: '400px', width: '100%' }}>
              <div className="director-image">
                <img src="/img/ramshibhai_goriya.png" alt="Mr. Ramshibhai Goriya" 
                     loading="lazy" decoding="async"
                     onError={(e) => {e.target.style.display='none'}} />
              </div>
              <h3>Mr. Ramshibhai Goriya</h3>
              <div className="designation">Founder & Chairman</div>
              <div className="director-description">
                Visionary entrepreneur who founded VR Group in 2016. With his leadership, the group has grown from 
                a single entity to a diversified business conglomerate, establishing a strong presence in renewable 
                energy and industrial sectors across Gujarat.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="section why-choose">
        <div className="container">
          <h2 className="section-title" style={{ color: 'white' }}>Our Core Values</h2>
          <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.9)' }}>
            The principles that guide our business operations and decision-making
          </p>
          
          <div className="features-grid-home">
            <div className="feature-item">
              <div className="feature-icon">🎯</div>
              <h3>Excellence</h3>
              <p>We strive for excellence in every project, maintaining the highest standards of quality and performance.</p>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">🤝</div>
              <h3>Integrity</h3>
              <p>Conducting business with transparency, honesty, and ethical practices in all our interactions.</p>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">🌱</div>
              <h3>Sustainability</h3>
              <p>Committed to sustainable business practices that benefit the environment and future generations.</p>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">💡</div>
              <h3>Innovation</h3>
              <p>Embracing cutting-edge technology and innovative solutions to stay ahead in competitive markets.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Future Outlook Section */}
      <section className="content-section">
        <div className="container">
          <div className="mission-vision">
            <div className="mv-card">
              <div className="mv-icon">🚀</div>
              <h3>Growth Strategy</h3>
              <p>
                VR Group is positioned for continued expansion across renewable energy and industrial sectors. 
                Our strategic focus includes scaling wind energy projects, expanding mining operations, and 
                exploring new opportunities in sustainable infrastructure development.
              </p>
            </div>
            
            <div className="mv-card">
              <div className="mv-icon">🌏</div>
              <h3>Sustainability Commitment</h3>
              <p>
                As we grow, our commitment to environmental responsibility remains unwavering. We integrate 
                sustainable practices across all business operations, ensuring our growth contributes positively 
                to India's sustainable development goals.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VRGroup;